import { useEffect, useState, useCallback } from 'react';
import { AppShell } from '../components/AppShell';
import { useConfirm } from '../components/ConfirmProvider';
import { LoadingIndicator } from '../components/LoadingIndicator';
import {
  listMyDevices,
  revokeDevice,
  revokeOtherDevices,
  type UserDevice,
} from '../lib/devices';

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function reasonLabel(reason: UserDevice['revokedReason']): string {
  switch (reason) {
    case 'user_revoke':      return 'You signed it out';
    case 'cap_exceeded':     return 'Auto-signed out (5-device limit)';
    case 'admin_revoke':     return 'Signed out by admin';
    case 'suspected_share':  return 'Flagged for review';
    default:                 return 'Signed out';
  }
}

export function Security() {
  const { confirm, alert } = useConfirm();
  const [devices, setDevices] = useState<UserDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDevices(await listMyDevices());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void refresh(); }, [refresh]);

  async function onRevoke(d: UserDevice) {
    if (d.isThisDevice) {
      await alert({
        title: 'Not this device',
        body: "You can't sign out the device you're using right now. Use the Sign out button at the bottom of Settings instead.",
      });
      return;
    }
    if (!(await confirm({
      title: `Sign out ${d.label || 'this device'}?`,
      body: "They'll need to log in again to use the app from there.",
      confirmLabel: 'Sign out',
      danger: true,
    }))) return;
    setBusyId(d.deviceId);
    try {
      await revokeDevice(d.deviceId);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function onRevokeOthers() {
    const count = devices.filter((d) => d.revokedAt === null && !d.isThisDevice).length;
    if (count === 0) {
      await alert({
        title: 'No other devices',
        body: "You're only signed in here — nothing to sign out.",
      });
      return;
    }
    if (!(await confirm({
      title: `Sign out all ${count} other device${count === 1 ? '' : 's'}?`,
      body: "You'll stay signed in on this one.",
      confirmLabel: 'Sign out others',
      danger: true,
    }))) return;
    setBusyId('all');
    try {
      await revokeOtherDevices();
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  const active   = devices.filter((d) => d.revokedAt === null);
  const inactive = devices.filter((d) => d.revokedAt !== null).slice(0, 10);

  return (
    <AppShell title="Sign-in security" back="/settings">
      {error && <div className="banner banner-error" style={{ marginBottom: 12 }}>{error}</div>}

      <div className="card" style={{ marginBottom: 12 }}>
        <p style={{ margin: 0, fontWeight: 600 }}>
          {active.length} active device{active.length === 1 ? '' : 's'} signed in to your account
        </p>
        <p className="muted" style={{ margin: '6px 0 0', fontSize: 13, lineHeight: 1.5 }}>
          We let you sign in on up to <strong>5 devices</strong> at once. If a 6th device signs in,
          we automatically sign out the oldest. If you suspect someone else has your password,
          tap <strong>Sign out other devices</strong> below — that boots everyone except this phone.
        </p>
      </div>

      {/* Active devices */}
      {loading ? (
        <LoadingIndicator />
      ) : active.length === 0 ? (
        <p className="muted">No active devices.</p>
      ) : (
        <ul className="person-list">
          {active.map((d) => (
            <li key={d.id}>
              <div style={{ padding: '12px 14px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <p style={{ margin: 0, fontWeight: 600 }}>
                    {d.label || 'Unknown device'}
                    {d.isThisDevice && (
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--success, #2F6B47)' }}>
                        · this device
                      </span>
                    )}
                  </p>
                  <span className="muted" style={{ fontSize: 11 }}>
                    last used {fmtDate(d.lastSeenAt)}
                  </span>
                </div>
                <p className="muted" style={{ margin: '2px 0 0', fontSize: 12 }}>
                  first signed in {fmtDate(d.firstSeenAt)}
                </p>
                {!d.isThisDevice && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    style={{ marginTop: 8, padding: '6px 10px', fontSize: 12, color: 'var(--danger)', borderColor: 'var(--danger)' }}
                    disabled={busyId === d.deviceId || busyId === 'all'}
                    onClick={() => onRevoke(d)}
                  >
                    {busyId === d.deviceId ? 'Signing out…' : 'Sign out this device'}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {active.length > 1 && (
        <button
          type="button"
          className="btn btn-ghost"
          style={{ width: '100%', marginTop: 16, color: 'var(--danger)', borderColor: 'var(--danger)' }}
          disabled={busyId === 'all'}
          onClick={onRevokeOthers}
        >
          {busyId === 'all' ? 'Signing out other devices…' : 'Sign out other devices →'}
        </button>
      )}

      {/* Recently signed out */}
      {inactive.length > 0 && (
        <>
          <h3 className="section-title" style={{ marginTop: 24 }}>Recently signed out</h3>
          <ul className="person-list">
            {inactive.map((d) => (
              <li key={d.id}>
                <div style={{ padding: '12px 14px' }}>
                  <p style={{ margin: 0, fontWeight: 600, color: 'var(--ink-soft)' }}>
                    {d.label || 'Unknown device'}
                  </p>
                  <p className="muted" style={{ margin: '2px 0 0', fontSize: 12 }}>
                    {reasonLabel(d.revokedReason)} · {fmtDate(d.revokedAt)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      <div className="card" style={{ marginTop: 24, background: 'var(--bg-soft, #FAF7F2)' }}>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--ink-soft)', lineHeight: 1.6 }}>
          <strong>Why we cap devices.</strong> Sharing a login between phones might feel cheaper than
          adding seats, but it breaks the audit trail Health &amp; Safety law requires. Every sign-in,
          RAMS signature and accident record gets stamped with the user account — shared logins look
          like one person at multiple sites simultaneously, which is exactly what HSE inspectors flag
          as fabricated records.
        </p>
      </div>
    </AppShell>
  );
}
